'use client';

import { BN, deriveMarketPda, deriveOutcomeMintPda } from '@morfi/contract-sdk';
import { PublicKey, VersionedTransaction } from '@solana/web3.js';
import { useEffect, useRef, useState } from 'react';
import { useAdvanceAdminMarketCreation } from '../../../../generated/mutations/useAdvanceAdminMarketCreation.gen.js';
import { useStartAdminMarketCreation } from '../../../../generated/mutations/useStartAdminMarketCreation.gen.js';
import { mergeCreationSignatures } from './admin-creation-signatures.js';
export function useAdminMarketCreation(args) {
  const start = useStartAdminMarketCreation();
  const advance = useAdvanceAdminMarketCreation();
  const [operation, setOperation] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const controller = useRef(null);
  useEffect(() => () => {
    controller.current?.abort();
    controller.current = null;
  }, []);
  // Each run is a fresh operation: the market nonce is drawn once and the
  // outcome mints follow from it as program PDAs. An interrupted creation
  // surfaces as an error and the operator starts again; nothing is persisted.
  async function run(draft) {
    if (controller.current !== null) return;
    const abort = new AbortController();
    controller.current = abort;
    setBusy(true);
    setError(null);
    try {
      const nonceBytes = crypto.getRandomValues(new Uint8Array(8));
      const nonce = new DataView(nonceBytes.buffer).getBigUint64(0, true).toString();
      const [market] = deriveMarketPda(new PublicKey(args.creatorAddress), new BN(nonce));
      const parameters = {
        ...draft,
        editorial: {
          ...draft.editorial,
          creatorPubkey: args.creatorAddress,
          marketNonceDecimal: nonce,
          outcomes: draft.editorial.outcomes.map(outcome => ({
            ...outcome,
            outcomeMintBase58: deriveOutcomeMintPda(market, outcome.outcomeIndex)[0].toBase58()
          }))
        }
      };
      const initialProofs = await proofs();
      let current = await start.mutateAsync({
        ...initialProofs,
        parameters
      });
      assertActive();
      while (!abort.signal.aborted) {
        setOperation(current);
        if (current.status === 'complete') return;
        let signedTransactionBase64;
        if (current.status === 'signing' && current.transactionBase64 !== null) {
          let tx = VersionedTransaction.deserialize(Buffer.from(current.transactionBase64, 'base64'));
          // The relay fee payer (first signer) pays fees and rent and signs server-side;
          // wallets sign only the slots they own.
          if (tx.version !== 0 || tx.message.staticAccountKeys[0]?.toBase58() !== current.feePayerPubkey) throw new Error('The transaction does not match the relay fee payer.');
          const required = tx.message.staticAccountKeys.slice(0, tx.message.header.numRequiredSignatures);
          if (required.some(pubkey => pubkey.toBase58() === args.payerAddress)) {
            const payerSigned = await args.signPayer(tx.serialize());
            assertActive();
            tx = mergeCreationSignatures(tx, payerSigned);
          }
          if (args.creatorAddress !== args.payerAddress && required.some(pubkey_0 => pubkey_0.toBase58() === args.creatorAddress)) {
            const creatorSigned = await requireCreatorSession().signTransaction(tx.serialize());
            assertActive();
            tx = mergeCreationSignatures(tx, creatorSigned);
          }
          if (tx.signatures.slice(1).some(signature => !signature.some(byte => byte !== 0))) throw new Error('The wallets did not return every required signature.');
          signedTransactionBase64 = Buffer.from(tx.serialize()).toString('base64');
        } else {
          await new Promise(resolve => setTimeout(resolve, 5000));
        }
        const refreshedProofs = await proofs();
        current = await advance.mutateAsync({
          ...refreshedProofs,
          id: current.id,
          ...(signedTransactionBase64 === undefined ? {} : {
            signedTransactionBase64
          })
        });
        assertActive();
      }
    } catch (cause) {
      if (!abort.signal.aborted) setError(cause instanceof Error ? cause.message : 'Creation interrupted. Check Platform markets or the creator before retrying.');
    } finally {
      if (controller.current === abort) {
        controller.current = null;
        setBusy(false);
      }
    }
    function assertActive() {
      if (abort.signal.aborted) throw new Error('Creation cancelled before it completed.');
    }
    async function proofs() {
      assertActive();
      const proof = await args.proof();
      assertActive();
      if (proof.payerAddress !== args.payerAddress) throw new Error('The paying wallet changed. Retry with the original wallet.');
      if (args.creatorAddress === args.payerAddress) return {
        proof
      };
      const creatorProof = await requireCreatorSession().proof();
      assertActive();
      if (creatorProof.payerAddress !== args.creatorAddress) throw new Error('The signing proof does not match the selected creator.');
      return {
        proof,
        creatorProof
      };
    }
  }
  function requireCreatorSession() {
    if (args.creatorSession === null || args.creatorSession.target.walletAddress !== args.creatorAddress) throw new Error('Authorize the selected creator to continue.');
    return args.creatorSession;
  }
  return {
    run,
    operation,
    busy,
    error
  };
}